import router from "@/router/router";
import storageService from "@/services/storage.service";
import { initKeycloakAuth, resetKeycloakAuthInit } from "@/composables/keycloak-auth.composable";
import { hasKeycloakConfig } from "@/lib/keycloak/config";
import {
  registerKeycloakAuthLogoutHandler,
  resetKeycloakSessionState,
  initKeycloakSession,
  getKeycloak,
} from "@/lib/keycloak";
import {
  clearLocalSessionForSsoLogout,
  hasPendingOAuthFlow,
  hasValidStoredSession,
  isKeycloakSsoSyncEnabled,
  isOAuthCallbackRoute,
  isSkipAutoKeycloakLoginPending,
  markSkipAutoKeycloakLoginOnNextVisit,
  probeKeycloakSilentSso,
  refreshKeycloakSessionOnReload,
} from "@/services/keycloak.service";

const SSO_POLL_MS = 4000;
const SSO_LOGOUT_MESSAGE = "traderverse-sso-logout";

let pollTimer: number | null = null;
let syncInFlight = false;

function shouldRunSsoSync(): boolean {
  if (!isKeycloakSsoSyncEnabled() || typeof window === "undefined") return false;
  if (isOAuthCallbackRoute() || hasPendingOAuthFlow()) return false;
  if (isSkipAutoKeycloakLoginPending()) return false;
  return true;
}

async function applyRemoteLogout(): Promise<void> {
  clearLocalSessionForSsoLogout();
  storageService.RemoveAllProperties();
  resetKeycloakAuthInit();
  resetKeycloakSessionState();
  markSkipAutoKeycloakLoginOnNextVisit();

  const current = router.currentRoute.value;
  const stayOnRoute = new Set(["signin", "accountBanned", "accountDeleted"]);
  if (current.name && stayOnRoute.has(String(current.name))) {
    return;
  }

  await router.replace({ name: "signin" });
}

async function applyRemoteLogin(): Promise<void> {
  if (!hasKeycloakConfig()) return;

  await initKeycloakSession();
  await initKeycloakAuth();

  const user = storageService.getUser();
  if (!user?._id) return;

  const current = router.currentRoute.value;
  if (current.name === "signin") {
    await router.replace({ name: "feed" });
    return;
  }

  const kc = getKeycloak();
  if (kc?.authenticated && !storageService.getToken()) {
    window.location.reload();
  }
}

async function runSsoSyncOnce(): Promise<void> {
  if (!shouldRunSsoSync() || syncInFlight) return;
  syncInFlight = true;
  try {
    const hasLocal = hasValidStoredSession();

    if (hasLocal) {
      if (hasKeycloakConfig()) {
        const kc = await initKeycloakSession();
        if (kc && !kc.authenticated) {
          await applyRemoteLogout();
          return;
        }
      }

      if ((await refreshKeycloakSessionOnReload()) === false) {
        await applyRemoteLogout();
        return;
      }
    }

    const probe = await probeKeycloakSilentSso();
    if (probe === "authenticated" && !hasValidStoredSession()) {
      await applyRemoteLogin();
      return;
    }
    if (probe === "unauthenticated" && hasLocal) {
      await applyRemoteLogout();
    }
  } catch {
    // ignore transient probe/network errors
  } finally {
    syncInFlight = false;
  }
}

function onSsoLogoutMessage(event: MessageEvent): void {
  if (event.origin !== window.location.origin) return;
  if (event.data !== SSO_LOGOUT_MESSAGE) return;
  void applyRemoteLogout();
}

function onVisibilityChange(): void {
  if (document.visibilityState === "visible") {
    void runSsoSyncOnce();
  }
}

export function initKeycloakSsoSync(): void {
  if (!isKeycloakSsoSyncEnabled() || typeof window === "undefined") return;

  registerKeycloakAuthLogoutHandler(() => {
    void applyRemoteLogout();
  });

  window.addEventListener("message", onSsoLogoutMessage);
  document.addEventListener("visibilitychange", onVisibilityChange);

  void runSsoSyncOnce();
  pollTimer = window.setInterval(() => {
    void runSsoSyncOnce();
  }, SSO_POLL_MS);
}

export function stopKeycloakSsoSync(): void {
  registerKeycloakAuthLogoutHandler(null);
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  window.removeEventListener("message", onSsoLogoutMessage);
  document.removeEventListener("visibilitychange", onVisibilityChange);
}
